/**
 * Assignment domain models for משימות שינוע 99 — the vehicle-bound crew.
 *
 * The one architectural rule this layer exists to enforce: a driver, a task
 * commander (מפקד משימה) or a passenger is never a loose name on a task. Every
 * person rides with a specific vehicle, so they are bound to a
 * {@link TaskTeam} — one team per vehicle, as many teams per task as the convoy
 * needs. There is no flat, unlinked crew list anywhere in the system.
 *
 * The rules that keep that binding honest live in
 * `src/core/assignment-rules.ts`; the offline store
 * (`src/db/assignment-store.ts`) and the assignment UI
 * (`src/modules/tasks/assignment/`) both build on the shapes here.
 */
import type { BaseEntity, ISODateString } from './index';

// ── אנשי חוץ — the word, and the one colour it is worn in ────────────
//
// A person the unit holds no roster record for is never silently mixed in with
// the company's own soldiers. Wherever one appears — a team card, the portrait
// order, the shared PNG, the WhatsApp text — they are named by the same word and
// carry the same mark, so a reader can tell at a glance who is on the מצבה and
// who was attached for this mission alone.

/** The label an external person is marked with, everywhere. */
export const EXTERNAL_PERSON_TAG = 'איש חוץ';

/** The category saved external people are filed under, separately. */
export const EXTERNAL_PEOPLE_CATEGORY = 'אנשי חוץ';

/** The heading of the vehicle an external person arrives in. */
export const TEMPORARY_VEHICLE_LABEL = 'רכב זמני';

/** The one tone the mark is worn in — violet, used by nothing else. */
export const EXTERNAL_PERSON_TONE = 'border-violet-300 bg-violet-50 text-violet-700';

// ── Task commander (מפקד משימה) ──────────────────────────────────────
//
// The seat is spelled `escort` in the code and מפקד משימה on every screen: the
// identifier is what the stored teams, the sync mirror and D1 have always been
// keyed by, and renaming it would rewrite records that are already in the
// field. The Hebrew below is the single place the unit's own word is set.

/**
 * What the manager decided about a task commander for this vehicle. `pending`
 * is a real decision — "we know one is needed, it has not been named yet" — and
 * is deliberately distinct from `none`.
 *
 * `external` is the fourth: the seat is filled, but by somebody the unit holds
 * no roster record for — a driver from another company, a contractor, a
 * reservist attached for the day. See {@link ExternalPerson}.
 */
export const ESCORT_OPTIONS = ['soldier', 'none', 'pending', 'external'] as const;

export type EscortOption = (typeof ESCORT_OPTIONS)[number];

/** The four choices, exactly as the assignment form offers them. */
export const ESCORT_OPTION_LABELS: Record<EscortOption, string> = {
  soldier: 'חייל כמפקד משימה',
  none: 'ללא מפקד משימה',
  pending: 'טרם נקבע מפקד משימה',
  external: 'איש חוץ כמפקד משימה',
};

/** Badge tone for the task commander slot on a team card. */
export const ESCORT_OPTION_TONES: Record<EscortOption, string> = {
  soldier: 'border-brand-300 bg-brand-50 text-brand-700',
  none: 'border-line bg-canvas text-ink/50',
  pending: 'border-amber-300 bg-amber-50 text-amber-800',
  external: EXTERNAL_PERSON_TONE,
};

/** A team starts out without a named task commander. */
export const DEFAULT_ESCORT_OPTION: EscortOption = 'pending';

// ── Crew roles ───────────────────────────────────────────────────────

export const CREW_ROLES = ['driver', 'escort', 'passenger'] as const;

export type CrewRole = (typeof CREW_ROLES)[number];

export const CREW_ROLE_LABELS: Record<CrewRole, string> = {
  driver: 'נהג',
  escort: 'מפקד משימה',
  passenger: 'נוסע',
};

// ── People ───────────────────────────────────────────────────────────

/**
 * Someone on the mission who is not on the unit's roster — a technician, a
 * contractor, a driver from another company, a reservist attached for the day.
 * Typed straight into the team form and carried by the team itself.
 *
 * An external person may hold any of the three seats, not only a passenger's:
 * `crewRole` on the row that carries them is what says which. They may also
 * arrive in a vehicle the unit does not own — `vehicleModel` and `plateNumber`
 * are that vehicle, recorded on the person rather than added to the fleet, so a
 * one-day loan never lands in the vehicle roster.
 */
export interface ExternalPerson {
  id: string;
  fullName: string;
  personalNumber?: string;
  /** Free text — the job or the body they come from ("טכנאי מנופים"). */
  role?: string;
  phone?: string;
  notes?: string;
  /** דגם הרכב הזמני they arrive in, when they bring one. */
  vehicleModel?: string;
  /** לוחית הרישוי of that same temporary vehicle. */
  plateNumber?: string;
  /** The `ExternalPersonRecord.id` they were taken from, once saved. */
  directoryId?: string;
}

/**
 * The name this shape was born under, when only a passenger could be external.
 * Kept so records and imports written against it keep type-checking.
 */
export type ExternalPassenger = ExternalPerson;

/**
 * One person on one vehicle. `teamId` is never empty: it is what binds the
 * person to the vehicle they actually ride with.
 *
 * Exactly one of `soldierId` / `externalPassenger` is set — a roster soldier or
 * a manually added rider.
 */
export interface TaskCrewMember {
  id: string;
  /** The vehicle team this person rides with. Never empty. */
  teamId: string;
  soldierId?: string;
  /** Set instead of `soldierId` for somebody off the roster. */
  externalPassenger?: ExternalPerson;
  crewRole: CrewRole;
}

// ── The vehicle team ─────────────────────────────────────────────────

/**
 * One vehicle on one task, with the crew bound to it. A task with three
 * vehicles carries three of these; nobody appears in more than one.
 *
 * `passengers` holds only the `passenger` rows — the driver and the escort are
 * named by `driverId` / `escortId` and are materialised as crew rows of their
 * own by `teamCrewMembers()`.
 */
export interface TaskTeam extends BaseEntity {
  taskId: string;
  vehicleId: string;
  driverId?: string;
  /**
   * An external driver, when the wheel is held by somebody off the roster.
   * Exactly one of `driverId` / `externalDriver` is ever set.
   */
  externalDriver?: ExternalPerson;
  escortOption: EscortOption;
  /** Only ever set when `escortOption` is `'soldier'`. */
  escortId?: string;
  /** Only ever set when `escortOption` is `'external'`. */
  externalEscort?: ExternalPerson;
  passengers: TaskCrewMember[];
  teamNote?: string;
  /**
   * Team metadata for every warning a manager assigned through anyway
   * ("שבץ בכל זאת"). Each entry names the conflict, the reason given and who
   * gave it, so the team itself carries the decision — the same records are
   * mirrored into the audit log.
   */
  overrides?: AssignmentOverride[];
}

/**
 * How a team is filed in Dexie: the crew rows live in `taskCrew` and are
 * rejoined on read, so the crew is never a copy embedded in two places.
 */
export type StoredTaskTeam = Omit<TaskTeam, 'passengers'>;

// ── Drafts (what the form submits) ───────────────────────────────────

export type ExternalPersonDraft = Omit<ExternalPerson, 'id'> & { id?: string };

/** The name the draft was born under — see {@link ExternalPassenger}. */
export type ExternalPassengerDraft = ExternalPersonDraft;

export interface TaskCrewMemberDraft {
  id?: string;
  teamId?: string;
  soldierId?: string;
  externalPassenger?: ExternalPersonDraft;
  crewRole?: CrewRole;
}

/** A team as the assignment form hands it over — ids and stamps are ours. */
export interface TaskTeamDraft {
  id?: string;
  vehicleId: string;
  driverId?: string;
  externalDriver?: ExternalPersonDraft;
  escortOption?: EscortOption;
  escortId?: string;
  externalEscort?: ExternalPersonDraft;
  passengers?: readonly TaskCrewMemberDraft[];
  teamNote?: string;
  overrides?: readonly AssignmentOverride[];
}

// ── Integrity conflicts ──────────────────────────────────────────────

/** Every way a crew can fail to be a legal, vehicle-bound crew. */
export const ASSIGNMENT_CONFLICT_CODES = [
  'MISSING_VEHICLE',
  'DUPLICATE_VEHICLE',
  'ESCORT_NOT_CHOSEN',
  'SOLDIER_IN_TWO_ROLES',
  'EMPTY_CREW_MEMBER',
  'EXTERNAL_PASSENGER_UNNAMED',
  'EXTERNAL_PERSON_UNNAMED',
  'OVERRIDE_WITHOUT_REASON',
] as const;

export type AssignmentConflictCode = (typeof ASSIGNMENT_CONFLICT_CODES)[number];

export const ASSIGNMENT_CONFLICT_LABELS: Record<AssignmentConflictCode, string> = {
  MISSING_VEHICLE: 'לא נבחר רכב לצוות — כל צוות חייב להיות משויך לרכב',
  DUPLICATE_VEHICLE: 'אותו רכב שובץ פעמיים לאותה משימה',
  ESCORT_NOT_CHOSEN: 'נבחר "חייל כמפקד משימה" אך לא נבחר חייל',
  SOLDIER_IN_TWO_ROLES: 'אותו חייל שובץ ליותר מתפקיד או רכב אחד במשימה',
  EMPTY_CREW_MEMBER: 'נוסע ללא חייל וללא פרטי נוסע חיצוני',
  EXTERNAL_PASSENGER_UNNAMED: 'נוסע חיצוני נוסף ללא שם',
  EXTERNAL_PERSON_UNNAMED: 'איש חוץ שובץ כנהג או כמפקד משימה ללא שם מלא',
  OVERRIDE_WITHOUT_REASON: 'שיבוץ בכפייה ("שבץ בכל זאת") נשמר ללא סיבה ומאשר',
};

export interface AssignmentConflict {
  code: AssignmentConflictCode;
  /** The team the conflict was found on. */
  teamId: string;
  /** Hebrew explanation, ready for the UI. */
  message: string;
}

// ── Operational conflicts (availability, time & licensing) ───────────

/**
 * What the operational check found. The three mandated kinds are a booking
 * that overlaps in time, a licence or permit the driver does not hold, and a
 * temporary soldier status still running when the task starts;
 * `VEHICLE_STATUS` covers the vehicle side of the same question (grounded, in
 * the workshop), which the manager needs to see for the very same decision.
 */
export const SCHEDULE_CONFLICT_TYPES = [
  'TIME_OVERLAP',
  'LICENSE_MISMATCH',
  'SOLDIER_STATUS',
  'VEHICLE_STATUS',
] as const;

export type ScheduleConflictType = (typeof SCHEDULE_CONFLICT_TYPES)[number];

export const SCHEDULE_CONFLICT_TYPE_LABELS: Record<ScheduleConflictType, string> = {
  TIME_OVERLAP: 'חפיפה בזמנים',
  LICENSE_MISMATCH: 'אי-התאמת רישיון או היתר',
  SOLDIER_STATUS: 'סטטוס זמני של החייל',
  VEHICLE_STATUS: 'מצב הרכב',
};

/**
 * How hard the warning pushes back. Neither level blocks the assignment — the
 * manager may always override — but a `CRITICAL` finding is one the system
 * believes makes the assignment illegal rather than merely awkward.
 */
export const CONFLICT_SEVERITIES = ['WARNING', 'CRITICAL'] as const;

export type ConflictSeverity = (typeof CONFLICT_SEVERITIES)[number];

export const CONFLICT_SEVERITY_LABELS: Record<ConflictSeverity, string> = {
  WARNING: 'אזהרה',
  CRITICAL: 'חמור',
};

export const CONFLICT_SEVERITY_TONES: Record<ConflictSeverity, string> = {
  WARNING: 'border-amber-300 bg-amber-50 text-amber-800',
  CRITICAL: 'border-rose-300 bg-rose-50 text-rose-700',
};

/** Whether the finding is about the person or about the vehicle. */
export type ConflictSubjectType = 'soldier' | 'vehicle';

/**
 * One operational finding about one candidate. `hasConflict` is always `true`
 * on a returned object — the engine only ever emits findings — so a caller can
 * hand a single conflict to the UI without re-deriving that fact.
 */
export interface ScheduleConflict {
  hasConflict: boolean;
  conflictType: ScheduleConflictType;
  severity: ConflictSeverity;
  /** Hebrew explanation with the exact times or the missing permit. */
  details: string;
  /**
   * `${subjectType}:${subjectId}:${conflictType}` — the stable identity an
   * override is filed against, so a decision survives a re-render.
   */
  key: string;
  subjectType: ConflictSubjectType;
  subjectId: string;
  /** The slot the soldier was being placed in, when there is one. */
  crewRole?: CrewRole;
  /** The task already holding the subject, on a `TIME_OVERLAP`. */
  conflictingTaskId?: string;
  /** `10:00-14:00` — the overlapping window, on a `TIME_OVERLAP`. */
  conflictingWindow?: string;
}

// ── "שבץ בכל זאת" — the override ─────────────────────────────────────

/** The reasons a manager may give for assigning over a warning. */
export const OVERRIDE_REASONS = [
  'STALE_DATA',
  'LICENSE_NOT_ENTERED',
  'PREVIOUS_TASK_ENDS_IN_TIME',
  'COMMANDER_APPROVED',
  'URGENT_TASK',
  'OTHER',
] as const;

export type OverrideReasonCode = (typeof OVERRIDE_REASONS)[number];

export const OVERRIDE_REASON_LABELS: Record<OverrideReasonCode, string> = {
  STALE_DATA: 'המידע במערכת אינו מעודכן',
  LICENSE_NOT_ENTERED: 'הרישיון/היתר קיים אך טרם הוזן',
  PREVIOUS_TASK_ENDS_IN_TIME: 'המשימה הקודמת צפויה להסתיים בזמן',
  COMMANDER_APPROVED: 'השיבוץ אושר על ידי מפקד',
  URGENT_TASK: 'המשימה דחופה',
  OTHER: 'סיבה אחרת',
};

/** The one reason that is not an answer on its own — it needs free text. */
export const OVERRIDE_REASON_REQUIRING_TEXT: OverrideReasonCode = 'OTHER';

/**
 * A manager taking responsibility for one warning. Stored twice on purpose:
 * on the team, so the convoy carries its own decisions, and in `auditLogs`,
 * so the ledger cannot be edited away by re-saving the team.
 */
export interface AssignmentOverride {
  id: string;
  /** The {@link ScheduleConflict.key} this override answers. */
  conflictKey: string;
  conflictType: ScheduleConflictType;
  severity: ConflictSeverity;
  subjectType: ConflictSubjectType;
  subjectId: string;
  /** Exactly the warning text the manager saw when they overrode it. */
  details: string;
  reason: OverrideReasonCode;
  /** Free text — mandatory for `OTHER`, optional otherwise. */
  reasonText?: string;
  /** The `User.id` that took responsibility. */
  managerId: string;
  createdAt: ISODateString;
}

/** The reason line as it reads in a banner or a log row. */
export function describeOverrideReason(override: AssignmentOverride): string {
  const label = OVERRIDE_REASON_LABELS[override.reason];
  return override.reasonText ? `${label} — ${override.reasonText}` : label;
}
